/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type { HostContext } from '../@types';
import Ingest from '@cchome/ingest-react';

import ProfileStorage from '../personalization/ProfileStorage';
import GlobalsUtils from '../utils/GlobalsUtils';
import { version } from '../pkgInfo.json';
import { getLogger } from '../utils/SearchLogger';
import WebStorage from '../storage/WebStorage';
import EventsService from '../services/EventsService';
import { Utils } from '../utils/Globals';
import SearchConfig from '../utils/SearchConfig';
/**
 * A class for initializing the Ingest module
 */

export default class AnalyticsUtil {
	readonly searchHostContext: HostContext;
	readonly ingest: Record<string, any>;
	static leftNavBarIndex: number;
	static leftNavNarLinks: Array<Record<string, any>>;
	static clientData: (...args: Array<any>) => any;
	static searchClient: string;
	static searchSurface: string;
	static experiment: Record<string, any>;
	static searchCampaignId: string;
	static searchVariationId: string;
	static searchPromptId: string;
	static clientSubcategory: string;
	static source: string;
	static appVersion: string;
	static location: string;
	static optOutAnalytics: boolean;
	static optOutMachineLearning: boolean;
	static locationVersion: string;
	static searchComponentVersion: string;
	static locationSetByLinks: string;
	static NERTrackingData: {};
	static analyticsConfig: Record<string, any>;

	constructor(searchHostContext: HostContext, withoutVisitorID = false) {
		this.searchHostContext = searchHostContext;
		this.ingest = new Ingest(this.initAnalytics(withoutVisitorID));
	}

	/**
	 *
	 */
	initAnalytics(withoutVisitorID: boolean) {
		const initObj: any = {};
		let clientData;
		initObj.xApiKey = 'ccsearch-component-service';
		initObj.apiKey = 'ccsearch-component-service';
		initObj.clientId = 'ccsearch-component-service';
		initObj.project = 'ccsearch-component-service';
		initObj.componentName = 'CC Search';
		initObj.componentVersion = version || 'version unknown';
		initObj.envComponentName = 'CC Search';
		initObj.envComponentVersion = version || 'version unknown';
		AnalyticsUtil.searchComponentVersion = version || 'version unknown';
		AnalyticsUtil.analyticsConfig = SearchConfig.getSearchAnalyticsConfig() ?? {};
		// $FlowFixMe	+++ change to analyticsData
		if (typeof this.searchHostContext.analyticsDataFunction === 'function') {
			clientData = this.searchHostContext.analyticsDataFunction();

			if (this.searchHostContext.hostInfo) {
				AnalyticsUtil.searchClient = this.searchHostContext.hostInfo.client;
				AnalyticsUtil.searchSurface = this.searchHostContext.hostInfo.surface;
			}

			if (clientData.analyticData) {
				GlobalsUtils.clientData = clientData;
				const analyticData = clientData.analyticData;

				if (analyticData.experiment) {
					AnalyticsUtil.experiment = Object.assign(AnalyticsUtil.experiment, analyticData.experiment);
				}

				if (analyticData.envComponentName) {
					initObj.envComponentName = analyticData.envComponentName;
				}

				if (analyticData.envComponentVersion) {
					initObj.envComponentVersion = analyticData.envComponentVersion;
				}

				if (analyticData.userIMSData) {
					initObj.userIMSData = analyticData.userIMSData;
				}

				if (typeof analyticData.optOutAnalytics === 'boolean') {
					initObj.optOutAnalytics = analyticData.optOutAnalytics;
					AnalyticsUtil.optOutAnalytics = analyticData.optOutAnalytics;
				}

				if (this.searchHostContext.imsInfo.optOutMachineLearning) {
					initObj.optOutMachineLearning = this.searchHostContext.imsInfo.optOutMachineLearning;
					AnalyticsUtil.optOutMachineLearning = this.searchHostContext.imsInfo.optOutMachineLearning;
				}

				if (analyticData.userAgent) {
					initObj.userAgent = analyticData.userAgent;
				}

				if (analyticData.source) {
					initObj.source = analyticData.source;
					AnalyticsUtil.source = analyticData.source;
				}

				if (analyticData.location) {
					AnalyticsUtil.location = analyticData.location;
				}

				if (analyticData.locationVersion) {
					AnalyticsUtil.locationVersion = analyticData.locationVersion;
				}

				if (analyticData.apiKey) {
					initObj.apiKey = analyticData.apiKey;
				}

				if (analyticData.path) {
					initObj.path = analyticData.path;
				}

				if (analyticData.version) {
					initObj.version = analyticData.version;
					AnalyticsUtil.appVersion = analyticData.version;
				}

				if (analyticData.platformType && this.searchHostContext.hostInfo.client === 'CCDesktop') {
					initObj.platformType = analyticData.platformType;
				} else {
					const platformTypeComputed = new Utils(this.searchHostContext).getOSName();
					initObj.platformType = platformTypeComputed;
				}

				if (analyticData.device) {
					initObj.device = analyticData.device;
				}

				if (typeof analyticData.offline === 'boolean') {
					initObj.offline = analyticData.offline;
				}

				if (analyticData.category) {
					initObj.category = analyticData.category;
				}

				if (analyticData.osVersion) {
					initObj.osVersion = analyticData.osVersion;
				}

				if (analyticData.planCode) {
					initObj.planCode = analyticData.planCode;
				}

				if (analyticData.stakeholder) {
					initObj.stakeholder = analyticData.stakeholder;
				}

				if (analyticData.campaignId) {
					AnalyticsUtil.searchCampaignId = analyticData.campaignId;
				}

				if (analyticData.variationId) {
					AnalyticsUtil.searchVariationId = analyticData.variationId;
				}

				if (analyticData.searchPromptId) {
					AnalyticsUtil.searchPromptId = analyticData.searchPromptId;
				}

				if (analyticData.clientSubcategory) {
					AnalyticsUtil.clientSubcategory = analyticData.clientSubcategory;
				}

				if (analyticData.sendEventsToHost && analyticData.sendEventsToHost === true) {
					initObj.sendEventsToHost = analyticData.sendEventsToHost;
				}

				if (analyticData.eventsCallBack) {
					initObj.eventsCallBack = analyticData.eventsCallBack;
				}

				if (analyticData.environment) {
					initObj.environment = analyticData.environment;
				} else {
					initObj.environment = this.searchHostContext.environment;
				}

				if (analyticData.dontPostAnalytics) {
					initObj.dontPostAnalytics = analyticData.dontPostAnalytics;
				}

				// Default to stage for all environments but 'production'
				if (initObj.environment === 'prod') {
					initObj.hostName = 'cc-api-data.adobe.io';
				} else {
					initObj.hostName = 'cc-api-data-stage.adobe.io';
				}

				if (withoutVisitorID) {
					initObj.timeOutForScripts = 0;
					initObj.sendWithOutVisitorID = true;
				}

				if (analyticData.sendWithOutVisitorID && analyticData.sendWithOutVisitorID === true) {
					initObj.sendWithOutVisitorID = true;
				}

				if (analyticData.useLocalReferrer) {
					initObj.useLocalReferrer = analyticData.useLocalReferrer;

					if (analyticData.referrerStorageName) {
						initObj.referrerStorageName = analyticData.referrerStorageName;
					}
				}

				this.setUserProfileInfo();
			}
		} else {
			getLogger().log('Analytics Util', 'SearchAnalytics.clientData is not defined');
		}

		return initObj;
	}

	/**
	 *
	 * @param {*} dataObject
	 */
	logAnalytics(dataObject: Record<string, any>) {
		Object.keys(dataObject).forEach((key) => {
			const value = dataObject[key];

			switch (key) {
				case 'setEventErrorType':
					this.ingest.setDataValue('event', 'error_type', value);
					break;

				case 'setEventErrorDesc':
					this.ingest.setDataValue('event', 'error_desc', value);
					break;

				case 'setEventWorkFlow':
					this.ingest.setDataValue('event', 'workflow', value);
					break;

				case 'setEventSubcategory':
					this.ingest.setDataValue('event', 'subcategory', value);
					break;

				case 'setEventType':
					this.ingest.setDataValue('event', 'type', value);
					break;

				case 'setEventSubtype':
					this.ingest.setDataValue('event', 'subtype', value);
					break;

				case 'setEventCount':
					this.ingest.setDataValue('event', 'count', value);
					break;

				case 'setEventContextGUID':
					this.ingest.setDataValue('event', 'context_guid', value);
					break;

				case 'setEventContextSequence':
					this.ingest.setDataValue('event', 'context_sequence', value);
					break;

				case 'setEventID':
					this.ingest.setDataValue('event', 'id', value);
					break;

				case 'setEventValue':
					this.ingest.setDataValue('event', 'value', value);
					break;

				case 'setContentID':
					this.ingest.setDataValue('content', 'id', value);
					break;

				case 'setContentName':
					this.ingest.setDataValue('content', 'name', value);
					break;

				case 'setContentMimeType':
					this.ingest.setDataValue('content', 'mimetype', value);
					break;

				case 'setContentType':
					this.ingest.setDataValue('content', 'type', value);
					break;

				case 'setContentCategory':
					this.ingest.setDataValue('content', 'category', value);
					break;

				case 'setContentAction':
					this.ingest.setDataValue('content', 'action', value);
					break;

				case 'setContentStatus':
					this.ingest.setDataValue('content', 'status', value);
					break;

				case 'setContentExtension':
					this.ingest.setDataValue('content', 'extension', value);
					break;

				case 'setUIFilter':
					this.ingest.setDataValue('ui', 'filter', value);
					break;

				case 'setUISortOrder':
					this.ingest.setDataValue('ui', 'sort_order', value);
					break;

				case 'setUIViewType':
					this.ingest.setDataValue('ui', 'view_type', value);
					break;

				case 'setUISearchKeyword':
					this.ingest.setDataValue('ui', 'search_keyword', value);
					break;

				case 'setUISequence':
					this.ingest.setDataValue('ui', 'sequence', value);
					break;

				case 'setUIPlaceholder':
					this.ingest.setDataValue('ui', 'search_placeholder', value);
					break;

				case 'setEnvironmentServiceName':
					this.ingest.setDataValue('env', 'svc.name', value);
					break;

				case 'setEnvironmentComponentName':
					this.ingest.setDataValue('env', 'com.name', value);
					break;

				case 'setExpCampaignID':
					this.ingest.setDataValue('exp', 'campaign_id', value);
					break;

				case 'setExpVariationID':
					this.ingest.setDataValue('exp', 'variation_id', value);
					break;

				default:
					getLogger().error('Analytics Util', 'Invalid key for Learning Service', key);
					break;
			}
		});
		this.ingest.addToCollection();
	}

	/**
	 * do we have an object
	 * @param {*} obj
	 */
	objectExists(obj: Record<string, any>) {
		if (obj !== undefined && obj !== null) {
			return true;
		}

		return false;
	}

	/**
	 * The name of the profile storage could change.
	 * So here we get it using ProfileStorage.getProfileStorageKey
	 * and then get the information we are interested in, and create
	 * an entry into session storage 'subscriptionPlan' that the
	 * ingest module will always use.
	 */
	setUserProfileInfo() {
		if (navigator.cookieEnabled && WebStorage.storageAvailable('sessionStorage')) {
			const cachedProfile: any = sessionStorage.getItem(ProfileStorage.getProfileStorageKey());
			let cachedProfileObj: any = {};

			if (this.objectExists(cachedProfile) && cachedProfile.length > 0) {
				try {
					cachedProfileObj = JSON.parse(cachedProfile);
				} catch (e) {
					getLogger().warn('AnalyticsUtil', 'Failed to parse cached profile from session storage', e);
					cachedProfileObj = {};
				}
			}

			if (
				this.objectExists(cachedProfileObj) &&
				Object.keys(cachedProfileObj).length !== 0 &&
				cachedProfileObj.subscription !== undefined
			) {
				/* eslint-disable */
				if (cachedProfileObj.subscription.planCode !== undefined) {
					let plan = cachedProfileObj.subscription.planCode;

					if (plan === 'unknown') {
						if (cachedProfileObj.subscription.status) {
							plan = cachedProfileObj.subscription.status;
						}
					}

					sessionStorage.setItem('subscriptionPlan', plan);
				}

				if (cachedProfileObj.subscription.stakeholder !== undefined) {
					const isStakeHolder = cachedProfileObj.subscription.stakeholder;
					sessionStorage.setItem('stakeholder', isStakeHolder);
				}
				/* eslint-enable */
			}
		}
	}

	static appendUrlWithAnalytics(inputUrl: string, isStock = false): string {
		// https://wiki.corp.adobe.com/pages/viewpage.action?pageId=1444405515
		let location = AnalyticsUtil.location;
		const locationVersion = AnalyticsUtil.searchComponentVersion;
		const analyticsParams = AnalyticsUtil.analyticsConfig?.params;
		let outputUrl = inputUrl;
		let params = `x-product=${encodeURIComponent(AnalyticsUtil.source)}`;

		if (AnalyticsUtil.appVersion?.length) {
			params = params.concat(encodeURIComponent(`/${AnalyticsUtil.appVersion}`));
		}

		if (location?.length) {
			if (!location.includes('Search:')) {
				location = `Search:${location}`;
			}

			params = params.concat(`&x-product-location=${encodeURIComponent(location)}`);
		}

		if (locationVersion?.length) {
			params = params.concat(encodeURIComponent(`/${locationVersion}`));
		}

		if (analyticsParams) {
			const defaultParams = analyticsParams?.default ?? '';
			params = params.concat(`&${defaultParams}`);
		}

		if (isStock) {
			params = params.concat(
				`&as_channel=${encodeURIComponent('adobe_apps')}&as_campclass=${encodeURIComponent(
					'brand',
				)}&as_source=${encodeURIComponent(AnalyticsUtil.source)}&as_camptype=${encodeURIComponent('app')}`,
			);

			if (location?.length) {
				params = params.concat(`&as_content=${encodeURIComponent(location)}`);
			}
		}

		AnalyticsUtil.location = '';
		AnalyticsUtil.locationVersion = '';
		outputUrl =
			typeof outputUrl === 'string' ? outputUrl.concat((outputUrl.split('?')[1] ? '&' : '?') + params) : '';
		return outputUrl;
	}

	sendRedirectAnalytic(eventType: string, url: string, reason: string, errordesc: string) {
		this.ingest.initializeDefaultData();
		const data: Record<string, any> = {
			setEventWorkFlow: 'Home',
			setEventType: eventType,
			setEventSubtype: reason,
			setContentAction: url,
		};

		if (errordesc) {
			data.setEventErrorDesc = errordesc;
		}

		this.logAnalytics(data);

		if (this.ingest.metricsCollection !== undefined && this.ingest.metricsCollection.length) {
			const eventsService = new EventsService(this.searchHostContext);
			eventsService.reportEvents(this.ingest.metricsCollection);
		}

		return this.ingest.sendToIngest();
	}
}
AnalyticsUtil.leftNavBarIndex = 0;
AnalyticsUtil.leftNavNarLinks = [{}];
AnalyticsUtil.searchClient = '';
AnalyticsUtil.searchSurface = '';
AnalyticsUtil.experiment = {};
AnalyticsUtil.searchCampaignId = '';
AnalyticsUtil.searchVariationId = '';
AnalyticsUtil.searchPromptId = '';
AnalyticsUtil.clientSubcategory = '';
AnalyticsUtil.source = '';
AnalyticsUtil.appVersion = '';
AnalyticsUtil.location = '';
AnalyticsUtil.optOutAnalytics = false;
AnalyticsUtil.optOutMachineLearning = false;
AnalyticsUtil.locationVersion = '';
AnalyticsUtil.searchComponentVersion = '';
AnalyticsUtil.locationSetByLinks = '';
AnalyticsUtil.NERTrackingData = {};
